import api from 'config/api.config';
import { AxiosResponse } from 'axios';
import { getErrorMessage } from 'utils/errors';

// Define interfaces for Proxy data structures
export interface Proxy {
  _id: string;
  proxy: string; // Format: username:password@ip:port or ip:port
  country: string;
  provider?: string;
  status: 'available' | 'in-use' | 'in_use' | 'disabled' | 'assigned_to_user';
  assignedTo?: string;
  lastUsed?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProxyUpload {
  proxy: string; // Format: username:password@ip:port or ip:port
  country: string;
  provider?: string;
}

// Helper function to parse proxy string
export const parseProxyString = (proxyString: string): {
  username?: string;
  password?: string;
  ip: string;
  port: number;
} => {
  // Format: username:password@ip:port or ip:port
  const hasAuth = proxyString.includes('@');

  if (hasAuth) {
    const [auth, address] = proxyString.split('@');
    const [username, password] = auth.split(':');
    const [ip, port] = address.split(':');
    return {
      username,
      password,
      ip,
      port: parseInt(port),
    };
  } else {
    const [ip, port] = proxyString.split(':');
    return {
      ip,
      port: parseInt(port),
    };
  }
};

// Helper function to build proxy string
export const buildProxyString = (
  ip: string,
  port: number,
  username?: string,
  password?: string
): string => {
  if (username && password) {
    return `${username}:${password}@${ip}:${port}`;
  }
  return `${ip}:${port}`;
};

export interface ProxiesListResponse {
  data: Proxy[];
  pagination: {
    current_page: number;
    per_page: number;
    total_pages: number;
    total_items: number;
  };
}

export interface ProxyFilters {
  page?: number;
  limit?: number;
  status?: 'available' | 'in-use' | 'in_use' | 'disabled' | 'assigned_to_user';
  country?: string;
  provider?: string;
  search?: string;
  sortBy?: 'createdAt' | 'lastUsed' | 'country' | 'status';
  sortOrder?: 'asc' | 'desc';
}

/**
 * 1. Upload single proxy
 * POST /api/proxies/upload
 */
export const uploadProxy = async (proxyData: ProxyUpload): Promise<Proxy> => {
  try {
    const response: AxiosResponse<Proxy> = await api.post(
      '/api/proxies/upload',
      proxyData
    );

    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, 'Failed to upload proxy'));
  }
};

/**
 * 2. Bulk upload proxies
 * POST /api/proxies/bulk-upload
 */
export const bulkUploadProxies = async (proxies: ProxyUpload[]): Promise<{
  success: number;
  failed: number;
  errors: string[];
}> => {
  try {
    const response: AxiosResponse<{
      success: number;
      failed: number;
      errors: string[];
    }> = await api.post(
      '/api/proxies/bulk-upload',
      { proxies }
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, 'Failed to bulk upload proxies'));
  }
};

/**
 * 3. Fetch available proxy by country
 * GET /api/proxies/fetch?country=XX
 */
export const fetchAvailableProxy = async (country: string): Promise<Proxy> => {
  try {
    const response: AxiosResponse<Proxy> = await api.get(
      '/api/proxies/fetch',
      {
        params: { country },
      }
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, 'Failed to fetch available proxy'));
  }
};

/**
 * 4. List all proxies with pagination and filters
 * GET /api/proxies/list
 */
export const listProxies = async (filters: ProxyFilters = {}): Promise<ProxiesListResponse> => {
  try {
    const params: Record<string, unknown> = {
      page: filters.page || 1,
      limit: filters.limit || 50,
    };

    if (filters.status) params.status = filters.status;
    if (filters.country) params.country = filters.country;
    if (filters.provider) params.provider = filters.provider;
    if (filters.search) params.search = filters.search;
    if (filters.sortBy) params.sortBy = filters.sortBy;
    if (filters.sortOrder) params.sortOrder = filters.sortOrder;

    const response: AxiosResponse<ProxiesListResponse> = await api.get(
      '/api/proxies/list',
      {
        params,
      }
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, 'Failed to fetch proxies list'));
  }
};

/**
 * 5. Get single proxy by ID
 * GET /api/proxies/:proxyId
 */
export const getProxyById = async (proxyId: string): Promise<Proxy> => {
  try {
    const response: AxiosResponse<Proxy> = await api.get(
      `/api/proxies/${proxyId}`
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, 'Failed to fetch proxy details'));
  }
};

/**
 * 6. Update proxy details
 * PUT /api/proxies/update/:proxyId
 */
export const updateProxy = async (
  proxyId: string,
  updates: ProxyUpload
): Promise<Proxy> => {
  try {
    const response: AxiosResponse<Proxy> = await api.put(
      `/api/proxies/update/${proxyId}`,
      updates
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, 'Failed to update proxy'));
  }
};

/**
 * 7. Release proxy back to available
 * PUT /api/proxies/release/:proxyId
 */
export const releaseProxy = async (proxyId: string): Promise<Proxy> => {
  try {
    const response: AxiosResponse<Proxy> = await api.put(
      `/api/proxies/release/${proxyId}`,
      {}
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, 'Failed to release proxy'));
  }
};

/**
 * 8. Disable a proxy
 * PUT /api/proxies/disable/:proxyId
 */
export const disableProxy = async (proxyId: string): Promise<Proxy> => {
  try {
    const response: AxiosResponse<Proxy> = await api.put(
      `/api/proxies/disable/${proxyId}`,
      {}
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, 'Failed to disable proxy'));
  }
};

/**
 * 9. Enable a proxy
 * PUT /api/proxies/enable/:proxyId
 */
export const enableProxy = async (proxyId: string): Promise<Proxy> => {
  try {
    const response: AxiosResponse<Proxy> = await api.put(
      `/api/proxies/enable/${proxyId}`,
      {}
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, 'Failed to enable proxy'));
  }
};

/**
 * 10. Delete single proxy
 * DELETE /api/proxies/delete/:proxyId
 */
export const deleteProxy = async (proxyId: string): Promise<{ message: string }> => {
  try {
    const response: AxiosResponse<{ message: string }> = await api.delete(
      `/api/proxies/delete/${proxyId}`
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, 'Failed to delete proxy'));
  }
};

/**
 * 11. Bulk delete proxies
 * POST /api/proxies/bulk-delete
 */
export const bulkDeleteProxies = async (proxyIds: string[]): Promise<{
  deletedCount: number;
  message: string;
}> => {
  try {
    const response: AxiosResponse<{
      deletedCount: number;
      message: string;
    }> = await api.post(
      '/api/proxies/bulk-delete',
      { proxyIds }
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, 'Failed to bulk delete proxies'));
  }
};

/**
 * Get proxy statistics
 * Helper function to get proxy counts by status
 * NOTE: This is an optional endpoint. If it's not implemented on your backend,
 * the UI will calculate stats from the current page data instead.
 * For accurate stats across all pages, implement GET /api/proxies/stats on your backend.
 */
export const getProxyStats = async (): Promise<{
  total: number;
  available: number;
  inUse: number;
  disabled: number;
  byCountry: Record<string, number>;
}> => {
  try {
    const response: AxiosResponse<{
      total: number;
      available: number;
      inUse: number;
      disabled: number;
      byCountry: Record<string, number>;
    }> = await api.get(
      '/api/proxies/stats'
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, 'Failed to fetch proxy statistics'));
  }
};
